import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ClienteInfoCard from "../GestDocumentos/GestorDocumentos/ClienteInfoCard";
import { FaFileAlt, FaCheckCircle, FaSpinner, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { APIURL } from "../../configApi/apiConfig";
import axios from "../../configApi/axiosConfig";
import { useAuth } from "../AuthContext/AuthContext";
import { enqueueSnackbar } from "notistack";

export function GestorDocumentosFirma() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [aprobandoDocumentacion, setAprobandoDocumentacion] = useState(false);
    const [modalConfirmarAprobar, setModalConfirmarAprobar] = useState(false);
    
    const [clientInfo, setClientInfo] = useState({
        id: "",
        nombre: "",
        cedula: "",
        fecha: "",
        almacen: "",
        foto: "",
        NumeroSolicitud: "",
        vendedor: "",
        consulta: "",
        permisos: [],
        sCre_SolicitudWeb: "",
        Estado: "",
        TipoFirmaElectronica: "",
        idEstadoVerificacionSolicitud: "",
        idEstadoVerificacionDocumental: "",
    });

    const [pdfs, setPdfs] = useState({});
    const [pdfUrls, setPdfUrls] = useState({});
    const [activeDocIndex, setActiveDocIndex] = useState(0);
    const [loadingPdfs, setLoadingPdfs] = useState(false);

    const documentosRequeridos = [
        { id: 1, key: "solicitudCupo", nombre: "Solicitud de Cupo", estado: "pendiente" },
        { id: 2, key: "reservaDominio", nombre: "Reserva de Dominio", estado: "pendiente" },
        { id: 3, key: "encargoFiduciario", nombre: "Encargo Fiduciario", estado: "pendiente" },
        { id: 4, key: "pagare", nombre: "Pagaré", estado: "pendiente" },
        { id: 5, key: "endoso", nombre: "Endoso", estado: "pendiente" },
    ];

    // Mapeo NombreDocumento (BD) → key del viewer
    const NOMBRE_DOC_KEY = {
        "Solicitud de Cupo":   "solicitudCupo",
        "Reserva de Dominio":  "reservaDominio",
        "Encargo Fiduciario":  "encargoFiduciario",
        "Pagare":              "pagare",
        "Pagaré":              "pagare",
        "Endoso":              "endoso",
        "Endoso Pagare":       "endoso",
    };

    const puedeVisualizarfirma = () => {
        const permisos = clientInfo.permisos || [];
        const permiso = permisos.find((p) => p.Permisos === "VISUALIZAR FIRMA");
        return permiso && permiso.Activo;
    };

    // El botón "Aprobar Documentación" aparece solo cuando el registro cumple
    // las tres condiciones: Estado=6, TipoFirmaElectronica=1, idEstadoVerificacionSolicitud=12
    const puedeAprobarDocumentacion =
        Number(clientInfo.Estado) === 6 &&
        Number(clientInfo.TipoFirmaElectronica) === 1 &&
        Number(clientInfo.idEstadoVerificacionSolicitud) === 12 &&
        Number(clientInfo.idEstadoVerificacionDocumental) !== 4;

    const handleAprobarDocumentacion = async () => {
        if (aprobandoDocumentacion) return;
        setAprobandoDocumentacion(true);
        try {
            // Actualiza idEstadoVerificacionDocumental = 4 (APROBACION)
            await axios.patch(APIURL.update_soliciutd_telefonica(clientInfo.id, 4));

            // Registro en la línea de tiempo — mismo patrón que GestorDocumentos.js
            await axios.post(APIURL.post_createtiemposolicitudeswebDto(), {
                idCre_SolicitudWeb: clientInfo.id,
                Tipo: 3,
                idEstadoVerificacionDocumental: 4,
                Usuario: userData?.Nombre,
            });

            enqueueSnackbar("Documentación aprobada correctamente.", { variant: "success" });
            // Actualiza el estado local para ocultar el botón si ya se aprobó
            setClientInfo((prev) => ({ ...prev, idEstadoVerificacionDocumental: 4 }));
        } catch (error) {
            console.error("Error al aprobar documentación:", error);
            enqueueSnackbar("Error al aprobar la documentación.", { variant: "error" });
        } finally {
            setAprobandoDocumentacion(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString('es-EC', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    useEffect(() => {
        if (state) {
            localStorage.setItem("clientInfoFirma", JSON.stringify(state));
            setClientInfo(state);
        } else {
            const savedClientInfo = localStorage.getItem("clientInfoFirma");
            if (savedClientInfo) {
                setClientInfo(JSON.parse(savedClientInfo));
            }
        }
    }, [state]);

    const [esFirmado, setEsFirmado] = useState(false);

    useEffect(() => {
        const fetchPdfs = async () => {
            const guid = clientInfo.sCre_SolicitudWeb;
            if (!guid) return;

            try {
                setLoadingPdfs(true);
                setEsFirmado(false);

                // 1️⃣ Intentar primero los documentos ya FIRMADOS (Cre_BiometriaCallbackLog)
                //    independientemente del valor de idEstadoVerificacionDocumental,
                //    porque los callbacks de TWS2 llegan de forma asincrónica y el campo
                //    podría no estar actualizado aún en clientInfo.
                try {
                    const firmadosResp = await axios.get(APIURL.get_documentos_firmados(guid));
                    const firmados = firmadosResp.data || [];

                    if (firmados.length > 0) {
                        // Hay documentos firmados → usarlos directamente (URL pública HTTPS)
                        const urls = {};
                        firmados.forEach(({ NombreDocumento, GsUri }) => {
                            const key = NOMBRE_DOC_KEY[NombreDocumento];
                            if (key && GsUri) urls[key] = GsUri;
                        });
                        setPdfs({});
                        setPdfUrls(urls);
                        setEsFirmado(true);
                        return; // no continuar con el path de sin-firmar
                    }
                } catch {
                    // Si el endpoint de firmados falla (no desplegado, etc.), ignorar
                    // y caer al path de documentos sin firmar.
                }

                // 2️⃣ Sin documentos firmados disponibles → generar PDFs al vuelo (base64)
                const response = await axios.post(APIURL.post_tws_documento_pdf(), {
                    sCre_SolicitudWeb: guid
                });

                if (response.data) {
                    const payload = response.data.data || response.data;
                    setPdfs(payload);

                    const urls = {};
                    for (const doc of documentosRequeridos) {
                        let pdfData = payload[doc.key];
                        if (pdfData && typeof pdfData === 'object' && !pdfData.error) {
                            pdfData = pdfData.base64 || pdfData.data || pdfData.documento || pdfData.pdf;
                        }
                        if (pdfData && typeof pdfData === 'string' && !payload[doc.key]?.error) {
                            try {
                                const base64String = pdfData.includes(',') ? pdfData.split(',')[1] : pdfData;
                                const cleanBase64 = base64String.replace(/\s/g, '');
                                const byteCharacters = atob(cleanBase64);
                                const byteNumbers = new Array(byteCharacters.length);
                                for (let i = 0; i < byteCharacters.length; i++) {
                                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                                }
                                const byteArray = new Uint8Array(byteNumbers);
                                const blob = new Blob([byteArray], { type: 'application/pdf' });
                                urls[doc.key] = URL.createObjectURL(blob);
                            } catch (e) {
                                console.error(`Error procesando PDF ${doc.key}:`, e);
                            }
                        }
                    }
                    setPdfUrls(urls);
                }
            } catch (error) {
                console.error("Error al obtener los PDFs:", error);
                const backendErrorMsg = error.response?.data?.message || error.response?.data || error.message || "Error desconocido de red o servidor";
                setPdfs({
                    solicitudCupo: { error: backendErrorMsg },
                    reservaDominio: { error: backendErrorMsg },
                    encargoFiduciario: { error: backendErrorMsg },
                    pagare: { error: backendErrorMsg },
                    endoso: { error: backendErrorMsg }
                });
            } finally {
                setLoadingPdfs(false);
            }
        };

        fetchPdfs();
    }, [clientInfo.sCre_SolicitudWeb, clientInfo.idEstadoVerificacionDocumental]); // eslint-disable-line react-hooks/exhaustive-deps
    const handleNext = () => {
        if (activeDocIndex < documentosRequeridos.length - 1) setActiveDocIndex(prev => prev + 1);
    };

    const handlePrev = () => {
        if (activeDocIndex > 0) setActiveDocIndex(prev => prev - 1);
    };

    const activeDoc = documentosRequeridos[activeDocIndex];
    const activeUrl = pdfUrls[activeDoc.key];
    const activeError = pdfs[activeDoc.key]?.error;

    return (
        <div className="flex flex-col min-h-[calc(100vh-64px)] bg-[#f8fafc] font-sans overflow-y-auto">
            <div className="flex-1 w-full p-2 md:p-4">
                <div className="w-full mx-auto space-y-4">
                    <div className="flex flex-col xl:flex-row gap-4">
                        {/* Left Side: Client Info */}
                        <div className="w-full xl:w-[700px] flex-shrink-0">
                            <div className="sticky top-4 transform transition-all duration-500 hover:scale-[1.01]">
                                <ClienteInfoCard 
                                    clientInfo={clientInfo} 
                                    formatDate={formatDate} 
                                    navigate={navigate} 
                                    permiso={puedeVisualizarfirma()} 
                                    compact={false}
                                />
                            </div>
                        </div>

                        {/* Right Side: PDF Viewer Section */}
                        <div className="flex-1 flex flex-col min-w-0">
                            <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(59,130,246,0.25)] border-2 border-blue-200 overflow-hidden flex flex-col h-full">
                                {/* Header */}
                                <div className="bg-slate-50 border-b border-slate-200 p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                        <FaFileAlt className="text-[#2d3689]" />
                                        {activeDoc.nombre}
                                    </h2>
                                    <div className="flex items-center gap-3">
                                        {puedeAprobarDocumentacion && (
                                            <button
                                                onClick={() => setModalConfirmarAprobar(true)}
                                                disabled={aprobandoDocumentacion}
                                                className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 active:scale-95 text-white text-sm font-semibold rounded-lg shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                <FaCheckCircle />
                                                Aprobar Documentación
                                            </button>
                                        )}
                                        <button
                                            onClick={handlePrev}
                                            disabled={activeDocIndex === 0}
                                            className={`p-2 rounded-full border transition-colors ${activeDocIndex === 0 ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-[#2d3689] border-blue-200 hover:bg-blue-50 hover:shadow-sm'}`}
                                        >
                                            <FaChevronLeft />
                                        </button>
                                        <span className="text-sm font-medium text-slate-600 px-3 py-1 bg-white border border-slate-200 rounded-full">
                                            {activeDocIndex + 1} de {documentosRequeridos.length}
                                        </span>
                                        <button 
                                            onClick={handleNext}
                                            disabled={activeDocIndex === documentosRequeridos.length - 1}
                                            className={`p-2 rounded-full border transition-colors ${activeDocIndex === documentosRequeridos.length - 1 ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-[#2d3689] border-blue-200 hover:bg-blue-50 hover:shadow-sm'}`}
                                        >
                                            <FaChevronRight />
                                        </button>
                                    </div>
                                </div>

                                {/* Viewer Body */}
                                <div className="h-[600px] xl:h-[700px] w-full bg-slate-200 flex items-center justify-center relative shadow-inner flex-1">
                                    {loadingPdfs ? (
                                        <div className="flex flex-col items-center text-slate-500">
                                            <FaSpinner className="animate-spin text-5xl text-[#2d3689] mb-4" />
                                            <p className="font-medium text-lg">Cargando documentos...</p>
                                        </div>
                                    ) : activeError ? (
                                        <div className="text-center px-6 bg-white p-8 rounded-2xl shadow-sm border border-red-100 max-w-md">
                                            <div className="bg-red-50 p-4 rounded-full inline-block mb-4">
                                                <FaFileAlt className="text-red-400 text-3xl" />
                                            </div>
                                            <p className="text-red-600 font-bold text-lg mb-2">Error al cargar {activeDoc.nombre}</p>
                                            <p className="text-slate-600 text-sm">{activeError}</p>
                                        </div>
                                    ) : activeUrl ? (
                                        <div className="w-full h-full flex flex-col relative">
                                            {esFirmado && (
                                                <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                                                    <FaCheckCircle />
                                                    Documento Firmado
                                                    <a
                                                        href={activeUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="ml-2 underline hover:text-green-100 transition-colors"
                                                    >
                                                        Abrir en nueva pestaña
                                                    </a>
                                                </div>
                                            )}
                                            <iframe
                                                src={activeUrl}
                                                className="w-full flex-1 border-none"
                                                title={activeDoc.nombre}
                                                onError={() => console.warn('iframe error loading', activeUrl)}
                                            />
                                        </div>
                                    ) : (
                                        <div className="text-center px-6 text-slate-500">
                                            <FaFileAlt className="text-5xl text-slate-300 mx-auto mb-4" />
                                            <p className="font-medium">Documento no disponible</p>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Bottom Buttons */}
                                <div className="bg-white border-t border-slate-100 p-4 overflow-x-auto shadow-[0_-4px_10px_rgba(0,0,0,0.02)]">
                                    <div className="flex items-center justify-center gap-3 min-w-max mx-auto">
                                        {documentosRequeridos.map((doc, idx) => {
                                            const isActive = idx === activeDocIndex;
                                            const hasError = pdfs[doc.key]?.error;
                                            return (
                                                <button
                                                    key={doc.id}
                                                    onClick={() => setActiveDocIndex(idx)}
                                                    className={`
                                                        flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 border-2
                                                        ${isActive ? 'bg-[#2d3689] text-white border-[#2d3689] shadow-md shadow-blue-900/20 transform scale-105' : 
                                                        hasError ? 'bg-white text-red-500 border-red-200 hover:bg-red-50' : 
                                                        'bg-white text-slate-600 border-slate-200 hover:border-[#2d3689]/30 hover:bg-slate-50 hover:text-[#2d3689]'}
                                                    `}
                                                >
                                                    {isActive ? <FaCheckCircle className="text-blue-200" /> : <FaFileAlt className={hasError ? "text-red-400" : "text-slate-400"} />}
                                                    {doc.nombre}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de confirmación: Aprobar Documentación */}
            {modalConfirmarAprobar && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 flex flex-col gap-5 animate-fadeIn">

                        {/* Icono + título */}
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                                <FaCheckCircle className="text-green-600 text-xl" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-800">
                                    Confirmar aprobación de documentos
                                </h3>
                                <p className="text-sm text-slate-500 mt-0.5">
                                    N° Solicitud: <span className="font-semibold text-slate-700">{clientInfo.NumeroSolicitud}</span>
                                </p>
                            </div>
                        </div>

                        {/* Mensaje de advertencia */}
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 leading-relaxed">
                            <p className="font-semibold mb-1">⚠️ Antes de continuar, verifique lo siguiente:</p>
                            <ul className="list-disc list-inside space-y-1 mt-2">
                                <li>Los documentos generados están correctos y sin errores.</li>
                                <li>Los datos del cliente, montos y condiciones son los acordados.</li>
                                <li>Una vez aprobados, estos documentos serán <span className="font-semibold">enviados al cliente para firma digital</span> y no podrán modificarse.</li>
                            </ul>
                        </div>

                        {/* Botones */}
                        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-1">
                            <button
                                onClick={() => setModalConfirmarAprobar(false)}
                                disabled={aprobandoDocumentacion}
                                className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl border-2 border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={async () => {
                                    setModalConfirmarAprobar(false);
                                    await handleAprobarDocumentacion();
                                }}
                                disabled={aprobandoDocumentacion}
                                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold text-sm shadow-md active:scale-95 transition-all disabled:opacity-60"
                            >
                                {aprobandoDocumentacion
                                    ? <><FaSpinner className="animate-spin" /> Aprobando...</>
                                    : <><FaCheckCircle /> Sí, aprobar documentos</>
                                }
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
